import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, TrendingUp, Users, Globe } from 'lucide-react';
import BlogCard from './BlogCard';
import { supabase } from '../../lib/supabase';

const BlogSection: React.FC = () => {
  const [blogs, setBlogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const dummyBlogs: any[] = [
    {
      id: 'blog-1',
      title: 'The Rise of Bangladesh\'s Textile Industry in Global Markets',
      slug: 'rise-bangladesh-textile-industry-global-markets',
      excerpt: 'Explore how Bangladesh has become a major player in the global textile industry, with insights into manufacturing capabilities, quality standards, and export growth.',
      featured_image: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
      category: 'industry-insights',
      tags: ['textile', 'export', 'manufacturing', 'global-trade'],
      published_at: '2024-01-15T00:00:00Z',
      author: {
        first_name: 'Nur',
        last_name: 'Mohammod'
      },
      read_time: 8
    },
    {
      id: 'blog-2',
      title: 'Sustainable Jute Products: Bangladesh\'s Green Revolution',
      slug: 'sustainable-jute-products-bangladesh-green-revolution',
      excerpt: 'Discover how Bangladeshi artisans are creating eco-friendly jute products that are gaining popularity worldwide as sustainable alternatives to plastic.',
      featured_image: 'https://images.pexels.com/photos/5217959/pexels-photo-5217959.jpeg',
      category: 'sustainability',
      tags: ['jute', 'sustainability', 'eco-friendly', 'environment'],
      published_at: '2024-01-20T00:00:00Z',
      author: {
        first_name: 'Romen',
        last_name: 'Raihan'
      },
      read_time: 6
    },
    {
      id: 'blog-3',
      title: 'From Dhaka to the World: Success Stories of Bangladeshi Exporters',
      slug: 'dhaka-world-success-stories-bangladeshi-exporters',
      excerpt: 'Read inspiring success stories of Bangladeshi businesses that have successfully expanded their reach to international markets through digital platforms.',
      featured_image: 'https://images.pexels.com/photos/7148621/pexels-photo-7148621.jpeg',
      category: 'success-stories',
      tags: ['success-stories', 'export', 'business-growth', 'international'],
      published_at: '2024-01-25T00:00:00Z',
      author: {
        first_name: 'Rifat',
        last_name: 'Raihan'
      },
      read_time: 10
    },
    {
      id: 'blog-4',
      title: 'Quality Standards and Certifications in Bangladesh Manufacturing',
      slug: 'quality-standards-certifications-bangladesh-manufacturing',
      excerpt: 'Learn about the various quality certifications and standards that Bangladeshi manufacturers adhere to for international compliance and customer satisfaction.',
      featured_image: 'https://images.pexels.com/photos/3683074/pexels-photo-3683074.jpeg',
      category: 'industry-insights',
      tags: ['quality', 'certification', 'standards', 'compliance'],
      published_at: '2024-01-30T00:00:00Z',
      author: {
        first_name: 'Hasan',
        last_name: 'Zilany'
      },
      read_time: 7
    }
  ];

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const blogsPerSlide = isMobile ? 1 : 4;
  const totalSlides = Math.ceil((blogs.length > 0 ? blogs.length : dummyBlogs.length) / blogsPerSlide);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        
        // Try to fetch from database
        const { data, error } = await supabase
          .from('blogs')
          .select(`
            *,
            users:author_id (
              first_name,
              last_name
            )
          `)
          .eq('status', 'published')
          .order('published_at', { ascending: false })
          .limit(4);
        
        if (error || !data || data.length === 0) {
          // Use dummy data if no real data
          setBlogs(dummyBlogs);
        } else {
          // Transform real data to match expected format
          const transformedData = data.map(item => ({
            ...item,
            author: item.users
          }));
          // Combine real data with dummy data for demo
          setBlogs(transformedData.length > 0 ? [...transformedData, ...dummyBlogs] : dummyBlogs);
        }
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setBlogs(dummyBlogs);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Auto-slide every 6 seconds
  useEffect(() => {
    const totalItems = blogs.length > 0 ? blogs.length : dummyBlogs.length;
    if (totalItems > blogsPerSlide) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [blogs.length, dummyBlogs.length, blogsPerSlide, totalSlides]);

  // Touch handlers for mobile swiping
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    const totalItems = blogs.length > 0 ? blogs.length : dummyBlogs.length;
    if (isLeftSwipe && totalItems > blogsPerSlide) {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }
    if (isRightSwipe && totalItems > blogsPerSlide) {
      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    }
  };

  // Use blogs if available, otherwise use dummy data
  const displayBlogs = blogs.length > 0 ? blogs : dummyBlogs;

  if (loading) {
    return (
      <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-lg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <BookOpen className="h-8 w-8 text-orange-600 mr-3" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Industry Insights
            </h2>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay informed with the latest trends, insights, and success stories from Bangladesh's thriving export industry
          </p>
          <div className="flex items-center justify-center space-x-6 mt-4 text-sm text-gray-600">
            <div className="flex items-center">
              <TrendingUp className="h-4 w-4 text-blue-600 mr-1" />
              <span>Market Trends</span>
            </div>
            <div className="flex items-center">
              <Users className="h-4 w-4 text-green-600 mr-1" />
              <span>Success Stories</span>
            </div>
            <div className="flex items-center">
              <Globe className="h-4 w-4 text-purple-600 mr-1" />
              <span>Global Insights</span>
            </div>
          </div>
        </div>

        {/* Blog Grid */}
        <div className="relative mb-12">
          {/* Navigation Buttons */}
          {displayBlogs.length > blogsPerSlide && !isMobile && (
            <>
              <button
                onClick={() => setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides)}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white transition-all duration-200 hover:scale-110"
              >
                <ChevronLeft className="h-6 w-6 text-gray-700" />
              </button>
              <button
                onClick={() => setCurrentSlide((prev) => (prev + 1) % totalSlides)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white transition-all duration-200 hover:scale-110"
              >
                <ChevronRight className="h-6 w-6 text-gray-700" />
              </button>
            </>
          )}

          {/* Blog Slider */}
          <div className="overflow-hidden rounded-xl">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{ 
                transform: `translateX(-${currentSlide * 100}%)`,
                width: `${totalSlides * 100}%`
              }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div 
                  key={slideIndex}
                  className={`flex ${isMobile ? 'space-x-0 px-4' : 'space-x-6 px-2'}`}
                  style={{ width: `${100 / totalSlides}%` }}
                >
                  {displayBlogs
                    .slice(slideIndex * blogsPerSlide, (slideIndex + 1) * blogsPerSlide)
                    .map((blog) => (
                      <div key={blog.id} className="flex-1">
                        <BlogCard blog={blog} />
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>

          {/* Slide Indicators */}
          {displayBlogs.length > blogsPerSlide && (
            <div className="flex justify-center mt-6 space-x-2">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    currentSlide === index 
                      ? 'bg-orange-600 scale-125' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link 
            to="/blog"
            className="inline-flex items-center space-x-2 bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors shadow-md"
          >
            <span>Read All Articles</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;